import * as z from "zod";
import { Id, IsoDate } from "./ids.js";

/** Organization roles. Owners and admins manage people and Spaces; members use the Spaces they are in. */
export const ORGANIZATION_ROLES = ["owner", "admin", "member"] as const;
export const OrganizationRoleSchema = z.enum(ORGANIZATION_ROLES);
export type OrganizationRole = z.infer<typeof OrganizationRoleSchema>;

/** Roles an invitation can grant; ownership is only ever handed over by an owner. */
export const InvitableRoleSchema = z.enum(["admin", "member"]);
export type InvitableRole = z.infer<typeof InvitableRoleSchema>;

/** Days an invitation link stays valid. */
export const INVITATION_TTL_DAYS = 7;

/** Request header that carries an invitation token on sign-up, so the invite admits the account. */
export const INVITATION_HEADER = "x-rakazo-invitation";

export function canManageTeam(role: string | null | undefined): boolean {
  return role === "owner" || role === "admin";
}

export const TeamMemberSchema = z.object({
  userId: Id,
  name: z.string(),
  email: z.string(),
  role: OrganizationRoleSchema,
  joinedAt: IsoDate,
  /** Suspended by an owner or admin: cannot open the organization, and their bots stop. */
  suspended: z.boolean().optional(),
  /** Bots they own in this organization, so an admin can hand them to someone else. */
  botCount: z.number().int().nonnegative().optional(),
});
export type TeamMember = z.infer<typeof TeamMemberSchema>;

export const TeamOrganizationSchema = z.object({
  id: Id,
  name: z.string(),
  role: OrganizationRoleSchema,
  /** The Space to open when switching to this organization; null when the user is in none of its Spaces. */
  spaceId: Id.nullable(),
});
export type TeamOrganization = z.infer<typeof TeamOrganizationSchema>;

export const TeamSchema = z.object({
  /** The caller. */
  userId: Id,
  organization: TeamOrganizationSchema,
  members: z.array(TeamMemberSchema),
  /** Every organization the user belongs to, for switching between them. */
  organizations: z.array(TeamOrganizationSchema),
  /** False when invitations cannot be emailed, so the admin shares the link instead. */
  inviteEmail: z.boolean(),
});
export type Team = z.infer<typeof TeamSchema>;

export const TeamInvitationSchema = z.object({
  id: Id,
  email: z.string(),
  role: InvitableRoleSchema,
  expiresAt: IsoDate,
});
export type TeamInvitation = z.infer<typeof TeamInvitationSchema>;

export const TeamSpaceSchema = z.object({
  id: Id,
  name: z.string(),
  isDefault: z.boolean(),
  memberUserIds: z.array(Id),
});
export type TeamSpace = z.infer<typeof TeamSpaceSchema>;

export const InvitationToken = z.string().min(16).max(128);

export const InvitationPreviewSchema = z.object({
  organizationName: z.string(),
  email: z.string(),
  hasAccount: z.boolean(),
});
export type InvitationPreview = z.infer<typeof InvitationPreviewSchema>;

export const InvitationEmail = z.string().trim().toLowerCase().email().max(254);
export const OrganizationName = z.string().trim().min(1).max(60);
